import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import { DemeterPageType, DemeterTableDefinitionType } from '../../../Generated/Raven-Demeter';
import UnitOfMeasureMetricImperialDropdown from '../../Components/Form/Inputs/UnitOfMeasureMetricImperialDropdown';
import TutorialLink from '../../Components/Form/Links/TutorialLink/TutorialLink';
import PageHeaderWithSelections from '../../Components/Headers/PageHeaderWithSelections';
import useTableDefinitionNavigation from '../../Components/Navigation/Hooks/useTableDefinitionNavigationHook';
import useUnitOfMeasure from '../../Core/Hooks/useUnitOfMeasureHook';
import useLanguage from '../../Services/Language/useLanguageHook';
import BalanceTable from './BalanceTable';
import BalanceTableChart from './BalanceTableChart';
import styles from './BalanceTablePage.module.scss';

export type BalanceType = 'domestic' | 'exports' | 'imports' | 'production' | 'stocks' | 'stocksCommercial' | 'stocksGovernment';

const BalanceTablePage: React.FC = () => {
    // Constants.
    const [translations] = useLanguage();
    const pageTitle = translations.balanceTable.title;
    const tableDefinitionType = DemeterTableDefinitionType.CommodityYearlyBalanceTable;

    // Hooks.
    const [, tableDefinitions, regionCommoditySelection] = useTableDefinitionNavigation(tableDefinitionType);
    const [searchParams] = useSearchParams();
    const [unitOfMeasure, setUnitOfMeasure] = useUnitOfMeasure('MetricImperial');
    const [balanceType, setBalanceType] = useState<BalanceType>('production');
    const [isPercent, setIsPercent] = useState(false);

    const sugarTypeLabels: Record<string, string> = {
        Sugar: translations.commodity.SugarTelQuel,
        SugarWhite: translations.commodity.SugarWhite,
        SugarRaw: translations.commodity.SugarRaw,
    };
    const sugarCommodity = tableDefinitions?.commodity ?? searchParams.get('commodity') ?? regionCommoditySelection?.commodity ?? '';
    const sugarRegion = searchParams.get('region') ?? regionCommoditySelection?.region ?? '';
    const sugarNote = sugarTypeLabels[sugarCommodity] || '';
    const chinaImportsNote = sugarNote && sugarRegion === 'China' ? translations.balanceTable.text.chinaImportsNote : '';
    const sugarNoteDisplay = chinaImportsNote ? `${sugarNote} - ${chinaImportsNote}` : sugarNote;

    return (
        <div className={styles.master_page_container}>
            <PageHeaderWithSelections
                title={pageTitle}
                tableDefinitionType={tableDefinitionType}
                rightSideOfHeaderComponent={
                    <>
                        <UnitOfMeasureMetricImperialDropdown
                            unitOfMeasure={unitOfMeasure}
                            handleChange={setUnitOfMeasure}
                            track={{ eventCategory: 'BalanceTable', eventAction: 'UnitOfMeasureChanged' }}
                        />
                        <TutorialLink link={applicationConstants.TutorialLinks.fundamentalsSupplyDemand} />
                    </>
                }
                testId="BalanceTablePageHeader"
                track={{ eventCategory: 'BalanceTable', eventAction: 'PageHeaderDisplayed' }}
                rightSideOfBreadcrumbsComponent={sugarNoteDisplay ? <span>{sugarNoteDisplay}</span> : undefined}
            />
            {regionCommoditySelection && (
                <BalanceTable
                    regionCommoditySelection={regionCommoditySelection}
                    unitOfMeasure={unitOfMeasure}
                    handleSelectBalanceType={(newbalanceType: BalanceType, newIsPercent: boolean) => {
                        setBalanceType(newbalanceType);
                        setIsPercent(newIsPercent);
                    }}
                    testId="BalanceTableTable"
                />
            )}
            <div className={styles.balance_table_chart_container}>
                {regionCommoditySelection && (
                    <BalanceTableChart
                        regionCommoditySelection={regionCommoditySelection}
                        unitOfMeasure={unitOfMeasure}
                        balanceType={balanceType}
                        isPercent={isPercent}
                        pageType={DemeterPageType.BalanceTable}
                        testId="BalanceTableChart"
                        track={{ eventCategory: 'BalanceTable', eventAction: 'BalanceTableChart' }}
                    />
                )}
            </div>
        </div>
    );
};

export default BalanceTablePage;
